import { type ValidationRule } from '../../types';
import { ActionRequiredError } from '../../util/errors';
import { validateCodeExpression } from '../../util/validators';

import { CanonicalStepRuleBase } from './base';

class CaptureRule extends CanonicalStepRuleBase {
  public constructor() {
    super('steps.Capture', 'Capture');
  }

  protected validate({ args }: any): void {
    this.checkSaveEquipmentId(args);
    this.checkSaveFilepath(args);
    this.checkFilepathPngExtension(args);
    this.checkFilepathExpression(args);
  }

  /**
   * Validate save equipment ID.
   * Throws ActionRequiredError if missing.
   *
   * @throws {ActionRequiredError}
   */
  private checkSaveEquipmentId(args: any): void {
    if (!args.saveEquipmentId) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Save equipment ID is required',
        fieldId: 'saveEquipmentId',
      });
    }
  }

  /**
   * Validate saveFilepath presence and (for constant kind) non-empty value.
   *
   * @throws {ActionRequiredError}
   */
  private checkSaveFilepath(args: any): void {
    if (!args.saveFilepath) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Save file path is required',
        fieldId: 'saveFilepath',
      });
    }

    if (
      args.saveFilepath.kind === 'constant' &&
      args.saveFilepath.value === ''
    ) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Save file path is required',
        fieldId: 'saveFilepath',
      });
    }
  }

  /**
   * Save filepath must resolve to a `.png` file. Constants must literally end
   * in `.png`; JS/Python expressions must end in `.png"` (closing string quote
   * implies the literal must end with `.png`).
   *
   * @throws {ActionRequiredError}
   */
  private checkFilepathPngExtension(args: any): void {
    const fp = args.saveFilepath;
    if (!fp) return;

    const endsWithPng =
      (fp.kind === 'constant' && fp.value.toString().endsWith('.png')) ||
      (fp.kind === 'JavaScript' && fp.expression.endsWith('.png"')) ||
      (fp.kind === 'Python' && fp.expression.endsWith('.png"'));

    if (
      fp.kind === 'constant' ||
      fp.kind === 'JavaScript' ||
      fp.kind === 'Python'
    ) {
      if (!endsWithPng) {
        throw new ActionRequiredError({
          kind: 'invalidConfiguration',
          message: 'Save file path must end with .png',
          fieldId: 'saveFilepath',
        });
      }
    }
  }

  /**
   * Validate saveFilepath expression structure.
   * Throws ActionRequiredError if invalid.
   *
   * @throws {ActionRequiredError}
   */
  private checkFilepathExpression(args: any): void {
    validateCodeExpression(args.saveFilepath, 'saveFilepath', 'Save filepath');
  }
}

export const CaptureValidation: ValidationRule =
  new CaptureRule().toValidationRule();
